import type { HTMLAttributes, ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { cn } from '../../lib/utils';

interface AuthShellProps {
  children: ReactNode;
  contentClassName?: string;
}

export function AuthCardTitle({ className, ...props }: HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h2
      className={cn('font-display text-2xl font-normal leading-[1.1] tracking-[-0.03em]', className)}
      {...props}
    />
  );
}

const decisionStages = [
  {
    label: 'Source',
    detail: 'Original deal materials',
  },
  {
    label: 'Analysis',
    detail: 'Cited diligence',
  },
  {
    label: 'Decision',
    detail: 'IC-ready record',
  },
];

/**
 * Shared signed-out frame for ReturnCatalyst account flows.
 *
 * Authentication pages keep ownership of their forms and handlers; this
 * component supplies only the branded, responsive page structure around them.
 */
export function AuthShell({ children, contentClassName }: AuthShellProps) {
  return (
    <main className="quantum-app min-h-screen bg-background text-foreground">
      <a
        href="#auth-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:bg-card focus:px-4 focus:py-3 focus:text-foreground focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
      >
        Skip to account access
      </a>

      <div className="mx-auto grid min-h-screen w-full max-w-[1440px] lg:grid-cols-[minmax(0,0.9fr)_minmax(440px,0.72fr)]">
        <aside className="relative flex min-h-[17rem] flex-col justify-between overflow-hidden border-b border-background/15 bg-foreground px-6 py-6 text-background sm:px-10 sm:py-8 lg:min-h-screen lg:border-b-0 lg:border-r lg:px-14 lg:py-10 xl:px-20 xl:py-12">
          <Link
            to="/"
            aria-label="ReturnCatalyst home"
            className="relative z-10 inline-flex w-fit items-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-background focus-visible:ring-offset-4 focus-visible:ring-offset-foreground"
          >
            <img
              src="/images/returncatalyst-logo.png"
              alt="ReturnCatalyst"
              width={168}
              height={42}
              decoding="async"
              className="h-auto w-28 sm:w-44"
            />
          </Link>

          <div className="relative z-10 mt-6 max-w-xl lg:my-auto lg:py-12">
            <p className="text-[11px] font-medium uppercase tracking-[0.22em] text-background/60">
              Private equity decision system
            </p>
            <h1 className="mt-4 max-w-none font-display text-[clamp(2.25rem,5vw,4.75rem)] font-normal leading-[0.98] tracking-[-0.045em] text-background lg:max-w-[12ch]">
              Every investment decision keeps its evidence.
            </h1>
            <p className="mt-5 hidden max-w-lg text-sm leading-6 text-background/65 sm:block sm:text-[15px]">
              ReturnCatalyst carries the record from source documents through diligence, committee review, and the hold period.
            </p>

            <ol
              aria-label="ReturnCatalyst decision workflow"
              className="mt-6 grid grid-cols-3 border-y border-background/15 lg:mt-12 lg:grid-cols-1 lg:border-b-0"
            >
              {decisionStages.map((stage, index) => (
                <li
                  key={stage.label}
                  className="relative border-r border-background/15 py-4 pr-3 last:border-r-0 lg:grid lg:grid-cols-[2rem_7rem_1fr] lg:items-center lg:gap-3 lg:border-b lg:border-r-0 lg:py-5"
                >
                  <span className="block font-mono text-[10px] text-background/40">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <span className="mt-1 block text-xs font-medium uppercase tracking-[0.16em] text-background sm:text-[13px] lg:mt-0">
                    {stage.label}
                  </span>
                  <span className="mt-1 hidden text-xs text-background/50 sm:block lg:mt-0">
                    {stage.detail}
                  </span>
                  {index < decisionStages.length - 1 && (
                    <span
                      aria-hidden="true"
                      className="absolute -bottom-px left-0 hidden h-px w-12 bg-primary lg:block"
                    />
                  )}
                </li>
              ))}
            </ol>
          </div>

          <p className="relative z-10 hidden text-xs text-background/45 lg:block">
            Documents. Diligence. IC. Portfolio.
          </p>
        </aside>

        <section
          id="auth-content"
          tabIndex={-1}
          aria-label="Account access"
          className="relative flex min-h-[calc(100vh-17rem)] items-center justify-center bg-background px-4 py-10 focus:outline-none sm:px-8 sm:py-14 lg:min-h-screen lg:px-12 xl:px-16"
        >
          <div className={cn('w-full max-w-[31rem]', contentClassName)}>{children}</div>
        </section>
      </div>
    </main>
  );
}
